import { ERP_SYSTEM_CONNECTION_TYPE } from '@helpers';
import { ColorFunction } from '@styles/colors/types';

export interface Connector {
  code: string;
  isEnabled: boolean;
  timeZoneIana: string;
  timeZoneWindows: string;
  documentsConnectionProperties?: {
    documentSources?: Array<{
      mappedNetworkDrive: string;
      networkPath: string;
    }>;
    documentsRootFolder?: string;
    credentials?: {
      password: string;
      userName: string;
    };
  };
  databaseOdbcConnectionProperties?: {
    host: string;
    database: string;
    dataSourceName: string;
    credentials?: {
      password: string;
      userName: string;
    };
  };
  databaseNpgsqlConnectionProperties?: {
    host: string;
    database: string;
    credentials?: {
      password: string;
      userName: string;
    };
  };
  databaseSqlClientConnectionProperties?: {
    serverEndpoint: string;
    databaseName: string;
    credentials?: {
      password: string;
      userName: string;
    };
  };
  apiConnectionProperties?: {
    apiKey: string;
    apiRootEndpoint: string;
    credentials: {
      password: string;
      userName: string;
    };
  };
  filterProperties?: Record<string, any>;
  hasDocumentsRootFolder: boolean;
  sourceSystemConnectionType: (typeof ERP_SYSTEM_CONNECTION_TYPE)[keyof typeof ERP_SYSTEM_CONNECTION_TYPE];
}

export const MODES = {
  SELECT_TYPE: 'SELECT_TYPE',
  CONNECTOR_DETAILS: 'CONNECTOR_DETAILS',
  DRIVERS_FOLDERS: 'DRIVERS_FOLDERS',
  TIME_ZONE: 'TIME_ZONE',
} as const;

export type Mode = (typeof MODES)[keyof typeof MODES];

export interface ProductionConnectorSetupProps {
  C: ColorFunction;
  hide: () => void;
  connectorDetails: Connector | null;
  erpSourceSystemTypes: (params: { filter: string }) => Promise<any[]>;
  connectorDetailsLoaded: Boolean;
  setStorage: (v: { [key: string]: boolean | Mode | Mode[] | Connector | null | undefined | {} }) => void;
  storage: {
    isEditMode: undefined;
    mode: undefined;
    modeHistory: [];
    data: {};
    dataHistory: {};
    isDataChanged: false;
  };
}

export interface MODES {
  setMode: (mode: string) => void;
  MODES: { [key: string]: string };
}

type UpdateDataFn = (key: string | Record<string, any>, value?: any) => void;
type UpdateDataObjFn = (obj: Record<string, any>) => void;
export type UpdateDataHistoryObjFn = (params: { changes: Record<string, unknown>; dataIsChanged?: boolean }) => void;

export interface UpdateData {
  updateData: UpdateDataFn;
}

export interface SelectTypeProps extends MODES, UpdateData {
  C: (key: string) => string;
  goBack: () => void;
  erpSourceSystemTypes: (params?: { filter?: string }) => Promise<any[]>;
}

export interface SystemType {
  code: string;
  name: string;
}

export interface FolderData {
  id: number;
  folder: string;
  UNC: string;
}

export interface DriversFoldersProps extends MODES, Pick<ConnectorDetailsProps, 'dataHistory'> {
  C: (key: string) => string;
  goBack: () => void;
  updateDataHistory: UpdateDataFn;
}

export interface Folder {
  id: number;
  folder: string;
  UNC: string;
}

export interface DatabaseConnectionField {
  hint: string;
  name: string;
}

export interface SelectedType {
  code: string;
  emptySourceSystemConfiguration: any;
  requiredFilterProperties: { code: string; name: string }[];
  hasDocumentsRootFolder: boolean;
  sourceSystemConnectionType: (typeof ERP_SYSTEM_CONNECTION_TYPE)[keyof typeof ERP_SYSTEM_CONNECTION_TYPE];
  sourceSystemConnectionFields: { hint: string; name: string }[];
}

export interface InputLine {
  key: string;
  name: string;
  placeholder: string;
  noArrow: boolean;
  isLable: boolean;
  editable?: boolean;
  placeholderAlreadyTranslated?: boolean;
}

export interface Data {
  folders?: Folder[];
  selectedType?: SelectedType;
  [key: string]: any;
}

export interface ConnectorDetailsProps extends MODES, UpdateData {
  C: ColorFunction;
  updateDataObj: UpdateDataObjFn;
  updateDataHistoryObj: UpdateDataHistoryObjFn;
  updateDataHistory: UpdateDataFn;
  goBack: () => void;
  data: Data;
  onboardProductionSourceSystem: (
    data: { sourceSystem: any; doRefreshApiCredentials?: boolean },
    onSuccess: () => void,
    onError: () => void
  ) => Promise<any>;
  maxHeight: number;
  connectorAlreadyExist: boolean;
  hide: () => void;
  connectorProductionConfigurationDetails: () => void;
  isEditMode: boolean;
  setIsEditMode: (v: boolean) => void;
  dataHistory: Data;
  setDataHistory: (v: boolean) => void;
  setData: (obj: Record<string, any>) => void;
  isDataChanged: boolean;
  setIsDataChanged: (v: boolean) => void;
  isError?: boolean;
}

export interface TimeZone {
  id: string;
  name: string;
}

export interface TimeZoneStorage {
  value: string;
  name: string;
}

export interface TimeZoneProps extends MODES {
  getTimezone: () => Promise<TimeZone[]>;
  user?: {
    userInfo?: {
      companyProfile?: { factoryTimeZone?: string };
    };
  };
  data: Connector;
  updateDataHistory: UpdateDataFn;
}
